import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import {
  Archive,
  ArrowRight,
  CircleCheck,
  CirclePause,
  CirclePlay,
  History,
  Pencil,
  Plus,
  ServerOff,
  Trash2,
  TriangleAlert,
} from "lucide-react";

import {
  archiveTenant,
  deleteStage,
  forceDeleteStage,
  getOnboardingUrl,
  getTenant,
  listDeployments,
  putStage,
  retryCognitoDomain,
  setLicenseStatus,
  triggerDeploy,
  triggerDestroy,
  type ConfirmBody,
  type TriggerDestroyBody,
} from "@/api/tenants";
import { isNotFound } from "@/api/client";
import { listBuilds } from "@/api/builds";
import { listGateways } from "@/api/catalog";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DeploymentTimeline } from "@/components/DeploymentTimeline";
import { Select } from "@/components/form";
import { KebabMenu, MenuDivider, MenuItem } from "@/components/KebabMenu";
import {
  ErrorBox,
  MutationError,
  RefreshFailed,
  isLoadingOrPaused,
} from "@/components/Spinner";
import { LaunchStackPanel } from "@/components/LaunchStackPanel";
import { RelativeTime } from "@/components/RelativeTime";
import { TenantNotFound } from "@/components/TenantNotFound";
import {
  DeploymentStatusBadge,
  ReadinessBadge,
  TenantStatusBadge,
} from "@/components/StatusBadge";
import {
  POLL_INTERVAL_MS,
  inflightCount,
  isActiveStatus,
  latestDeploymentByStage,
} from "@/lib/deployments";
import { formatDate, formatDateTime, fromEpochSeconds } from "@/lib/dates";
import { inDays, signingCertificateExpiry } from "@/lib/signingCertificate";
import { SSO_MODE_LABEL } from "@/lib/ssoMode";
import type {
  Deployment,
  GatewayCatalogItem,
  GatewayInstanceKind,
  TenantMetadata,
  TenantStage,
} from "@/types/api";
import {
  Button,
  ButtonLink,
  CardSkeleton,
  EmptyState,
  Icon,
  LoadingRegion,
  Page,
  PageHeader,
  Skeleton,
  StatusPill,
  TONE_FILL,
  TONE_INSET,
  TONE_SURFACE,
} from "@/components/ui";

export function TenantDetailPage() {
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Live deployment feed for this tenant. Polls every 5s while at least one
  // run is in flight, then stops; suspended in background tabs so a page left
  // open overnight doesn't burn invocations. This is what makes the dashboard
  // feel "live" — the operator stays here instead of bouncing to the history.
  const deploymentsQuery = useQuery({
    queryKey: ["deployments", tenantId],
    queryFn: () => listDeployments(token, tenantId, { limit: 50 }),
    enabled: !!token && !!tenantId,
    refetchInterval: (q) =>
      inflightCount(q.state.data?.items ?? []) > 0 ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });
  const inflight = inflightCount(deploymentsQuery.data?.items ?? []);

  const query = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => getTenant(token, tenantId),
    enabled: !!token && !!tenantId,
    // Keep the tenant snapshot fresh while a run is in flight so deployed
    // versions / destroyed flags update without a manual refresh.
    refetchInterval: inflight > 0 ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });

  // When the last in-flight run settles, pull the tenant once more so the
  // terminal state (deployed build, destroyed flag) lands immediately rather
  // than on the next idle refetch.
  const prevInflight = useRef(inflight);
  useEffect(() => {
    if (prevInflight.current > 0 && inflight === 0) {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
    }
    prevInflight.current = inflight;
  }, [inflight, qc, tenantId]);

  // Deep-link from the sidebar tree: ``/tenants/:id#stage-<name>`` scrolls the
  // matching StageCard into view once the tenant data has loaded. Additive —
  // the page behaves identically without a hash.
  useEffect(() => {
    if (!location.hash || query.isLoading) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, query.isLoading, query.data]);

  // The allowlists on a tenant reference catalog entries by (model, version);
  // deleting a catalog entry while a tenant still allowlists it leaves an
  // orphan reference. Fetch the catalog in parallel to flag orphans visually.
  const catalogQuery = useQuery({
    queryKey: ["catalog", "gateways"],
    queryFn: () => listGateways(token),
    enabled: !!token,
  });
  const catalogIndex = buildCatalogIndex(catalogQuery.data?.items ?? []);

  const [showArchive, setShowArchive] = useState(false);
  const archiveMut = useMutation({
    mutationFn: () => archiveTenant(token, tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      navigate("/");
    },
  });

  const retryDomainMut = useMutation({
    mutationFn: () => retryCognitoDomain(token, tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
    },
  });

  // The header is there while the tenant loads (its name is still a bar of the title's size) and
  // when it fails (the address gives the identifier): the way back never disappears with the data.
  const back = { to: "/tenants", label: "Tenants" };
  if (isLoadingOrPaused(query)) {
    return (
      <Page>
        <PageHeader title={undefined} back={back} subtitle={<Skeleton className="h-5 w-40" />} />
        <LoadingRegion className="space-y-6">
          <CardSkeleton />
          <CardSkeleton />
        </LoadingRegion>
      </Page>
    );
  }
  // Only an error with nothing to show replaces the page: a failed poll or
  // post-action refetch must not unmount the stage cards and their open modals.
  // A 404 is its own news (no such tenant: a stale bookmark, a typo); any other failure keeps the page
  // named by the identifier, with the reason.
  if (query.isLoadingError && isNotFound(query.error)) return <TenantNotFound tenantId={tenantId} />;
  if (query.isLoadingError) {
    return (
      <Page>
        <PageHeader title={tenantId} back={back} />
        <ErrorBox error={query.error} />
      </Page>
    );
  }
  if (!query.data) return null;

  const { metadata, stages } = query.data;
  const latestByStage = latestDeploymentByStage(
    deploymentsQuery.data?.items ?? [],
  );
  const loginReady =
    metadata.authProvisioned && metadata.cognitoDomainStatus === "active";

  return (
    <Page>
      {/* Sticky: this is a long page inside a scrolling <main>, and the
          refetch that fails is usually the one after an action taken lower
          down. ``deploymentsQuery`` is the POLLED one — it feeds the progress
          panel and the run state — so a failing poll must be flagged too. */}
      {(query.isRefetchError || deploymentsQuery.isRefetchError) && (
        <div className="sticky top-0 z-10 mb-6 space-y-2">
          <RefreshFailed query={query} />
          <RefreshFailed query={deploymentsQuery} />
        </div>
      )}
      <PageHeader
        back={back}
        title={metadata.displayName}
        subtitle={
          <a
            href={`https://${metadata.domain}`}
            target="_blank"
            rel="noreferrer noopener"
            className="hover:underline"
          >
            {metadata.domain}
          </a>
        }
        status={<TenantStatusBadge status={metadata.status} />}
        actions={
          <>
            {/* Always there: the history of every run of this tenant. The "History" link of a run in
                progress or failed (below) is only the shortcut to it at the moment it matters — without this
                button the page had no way in on a tenant whose last run succeeded. */}
            <ButtonLink
              to={`/tenants/${metadata.tenantId}/deployments`}
              variant="secondary"
              icon={History}
            >
              Deployment history
            </ButtonLink>
            <ButtonLink
              to={`/messaging/emails?tenant=${encodeURIComponent(metadata.tenantId)}`}
              variant="secondary"
            >
              Emails
            </ButtonLink>
            <ButtonLink to={`/tenants/${metadata.tenantId}/edit`} variant="secondary">
              Edit tenant
            </ButtonLink>
            <ButtonLink to={`/tenants/${metadata.tenantId}/identity-providers`} variant="secondary">
              Identity providers
            </ButtonLink>
            {metadata.status !== "archived" && (
              <KebabMenu label="Tenant actions">
                <MenuItem
                  icon={Archive}
                  disabled={archiveMut.isPending}
                  onClick={() => setShowArchive(true)}
                >
                  Archive tenant…
                </MenuItem>
              </KebabMenu>
            )}
          </>
        }
      />

      <div className="space-y-8">
        <div className="card">
          <h2 className="mb-4 text-lg font-medium text-ink">Information</h2>
          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <DetailRow
              label="Admin contact"
              value={`${metadata.adminGivenName} ${metadata.adminFamilyName} <${metadata.adminEmail}>`}
            />
            <div>
              <dt className="text-xs uppercase tracking-wide text-ink-muted">
                Login
              </dt>
              <dd className="mt-0.5 flex items-center gap-2 text-ink">
                <ReadinessBadge ready={loginReady} />
                {metadata.cognitoCustomDomainUrl ? (
                  <a
                    href={metadata.cognitoCustomDomainUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-xs underline decoration-dotted hover:text-ink"
                  >
                    Login page
                  </a>
                ) : (
                  <span className="text-xs text-ink-muted">
                    Standard login page
                  </span>
                )}
                {!loginReady && metadata.authProvisioned && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={retryDomainMut.isPending}
                    onClick={() => retryDomainMut.mutate()}
                  >
                    {retryDomainMut.isPending ? "Retrying…" : "Retry"}
                  </Button>
                )}
              </dd>
            </div>
            {/* Tenant-wide, not per stage: one Cognito pool serves every stage,
                so this belongs beside "Login" rather than in a StageCard. */}
            <DetailRow
              label="Two-factor authentication"
              value={metadata.mfaRequired ? "Required" : "Optional"}
            />
            <DetailRow label="Sign-in" value={SSO_MODE_LABEL[metadata.ssoMode]} />
            <DetailRow label="Emergency accounts" value={String(metadata.emergencyAccounts.length)} />
            {/* Tenant-wide like the second factor: one pool, every stage. */}
            <DetailRow
              label="Identity providers"
              value={
                metadata.identityProviders.length === 0
                  ? "None"
                  : metadata.identityProviders
                      .map(
                        (p) =>
                          `${p.displayName} (${p.roleSource === "directory" ? "roles from directory" : "by invitation"})` +
                          certificateSuffix(p.signingCertificateExpiresAt),
                      )
                      .join(", ")
              }
            />
          </dl>
          <MutationError mutation={retryDomainMut} title="Could not retry the login domain" className="mt-3" />
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-medium text-ink">Stages</h2>
            <ButtonLink
              to={`/tenants/${metadata.tenantId}/stages/new`}
              variant="secondary"
              icon={Plus}
            >
              New stage
            </ButtonLink>
          </div>
          {stages.length === 0 && (
            <div className="card">
              <EmptyState size="inline" title="No stages yet.">
                Create one before you can deploy.
              </EmptyState>
            </div>
          )}
          <div className="space-y-3">
            {stages.map((stage) => (
              <div
                key={stage.stage}
                id={`stage-${stage.stage}`}
                className="scroll-mt-4"
              >
                <StageCard
                  tenantId={metadata.tenantId}
                  stage={stage}
                  tenantHasProdStage={stages.some((s) => s.isProduction)}
                  catalogIndex={catalogIndex}
                  latestDeployment={latestByStage.get(stage.stage)}
                  token={token}
                />
              </div>
            ))}
          </div>
        </div>

        <TechnicalDetails metadata={metadata} stages={stages} />

        {showArchive && (
          <ConfirmModal
            title={`Archive "${metadata.displayName}"`}
            confirmLabel="Archive"
            pendingLabel="Archiving…"
            pending={archiveMut.isPending}
            error={archiveMut.error}
            onCancel={() => {
              archiveMut.reset();
              setShowArchive(false);
            }}
            onConfirm={() => archiveMut.mutate()}
          >
            <p>
              The tenant will be hidden from the default list but kept in the
              registry. This action is reversible.
            </p>
          </ConfirmModal>
        )}
      </div>
    </Page>
  );
}

function StageCard({
  tenantId,
  stage,
  tenantHasProdStage,
  catalogIndex,
  latestDeployment,
  token,
}: {
  tenantId: string;
  stage: TenantStage;
  // True when *any* sibling stage on this tenant is flagged production. The
  // Force-delete escape hatch is blocked even on non-prod stages of a prod
  // tenant — operators expect tenant-level safety in R&D.
  tenantHasProdStage: boolean;
  catalogIndex: CatalogIndex;
  // Most recent run for this stage, if any — drives the live progress panel.
  latestDeployment?: Deployment;
  token: string;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const awaitingClaim = stage.awaitingClaim;

  const onboardingQuery = useQuery({
    queryKey: ["onboarding-url", tenantId, stage.stage],
    queryFn: () => getOnboardingUrl(token, tenantId, stage.stage),
    enabled: awaitingClaim,
  });

  // Re-PUTting the stage refreshes the Launch URL: backend keeps the existing
  // registration_token if still valid, or re-mints it if expired. We echo the
  // stage's current business fields back — an empty body would let Pydantic
  // defaults clobber them. ``vpc_cidr`` is omitted on purpose: it's no longer
  // sent to the GUI and is now server-managed, so omitting it preserves the
  // stored value.
  const regenerateMut = useMutation({
    mutationFn: () =>
      putStage(token, tenantId, stage.stage, {
        aws_region: stage.awsRegion,
        is_production: stage.isProduction,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      qc.invalidateQueries({
        queryKey: ["onboarding-url", tenantId, stage.stage],
      });
    },
  });

  // Build picker. Production stages may only deploy production-channel builds;
  // other stages see every build. Empty selection = "latest production".
  const [selectedBuildId, setSelectedBuildId] = useState<string>("");
  const buildsQuery = useQuery({
    queryKey: ["builds", stage.isProduction ? "production" : "all"],
    queryFn: () =>
      listBuilds(token, stage.isProduction ? "production" : undefined),
    enabled: !!token && !awaitingClaim,
  });

  const deployMut = useMutation({
    mutationFn: () =>
      triggerDeploy(token, tenantId, stage.stage, selectedBuildId || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      qc.invalidateQueries({ queryKey: ["deployments", tenantId] });
      qc.invalidateQueries({ queryKey: ["deployments", "recent"] });
    },
  });

  const [showDestroyConfirm, setShowDestroyConfirm] = useState(false);
  // Une INTENTION plutôt qu'un booléen : la modale sert les deux sens. Reprendre
  // une licence de production exige la clé de production autant que la
  // suspendre — le backend appelle ``require_production_key`` avant de brancher
  // sur le statut, donc une reprise sans clé rendrait 403 pour toujours.
  const [licenseIntent, setLicenseIntent] = useState<
    null | "active" | "suspended"
  >(null);
  const suspended = stage.licenseStatus === "suspended";
  const licenseMut = useMutation({
    mutationFn: ({
      status,
      productionKey,
    }: {
      status: "active" | "suspended";
      productionKey?: string;
    }) => setLicenseStatus(token, tenantId, stage.stage, status, productionKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      setLicenseIntent(null);
    },
  });
  const destroyMut = useMutation({
    mutationFn: ({
      body,
      productionKey,
    }: {
      body: TriggerDestroyBody;
      productionKey?: string;
    }) => triggerDestroy(token, tenantId, stage.stage, body, productionKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      qc.invalidateQueries({ queryKey: ["deployments", tenantId] });
      qc.invalidateQueries({ queryKey: ["deployments", "recent"] });
      setShowDestroyConfirm(false);
    },
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteStageMut = useMutation({
    mutationFn: ({
      body,
      productionKey,
    }: {
      body: ConfirmBody;
      productionKey?: string;
    }) => deleteStage(token, tenantId, stage.stage, body, productionKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      setShowDeleteConfirm(false);
    },
  });

  // R&D escape hatch that skips the destroy/active-deployment guards. The
  // backend keeps a recovery window on prod; the front additionally refuses
  // the action on any stage of a prod tenant.
  const [showForceDeleteConfirm, setShowForceDeleteConfirm] = useState(false);
  const forceDeleteStageMut = useMutation({
    mutationFn: ({
      body,
      productionKey,
    }: {
      body: TriggerDestroyBody;
      productionKey?: string;
    }) => forceDeleteStage(token, tenantId, stage.stage, body, productionKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      qc.invalidateQueries({ queryKey: ["deployments", tenantId] });
      qc.invalidateQueries({ queryKey: ["deployments", "recent"] });
      setShowForceDeleteConfirm(false);
    },
  });

  const anyDestructivePending =
    destroyMut.isPending ||
    deleteStageMut.isPending ||
    forceDeleteStageMut.isPending;

  const forceDeleteBlocked = stage.isProduction || tenantHasProdStage;

  const deleteHistoryBlocked =
    deleteStageMut.isError &&
    /has deployment history/i.test(
      (deleteStageMut.error as Error | undefined)?.message ?? "",
    );

  const environmentReady = stage.environmentClaimed;
  const connectionReady = stage.loginReady;
  const expected = `${tenantId}/${stage.stage}`;

  const runActive =
    latestDeployment != null && isActiveStatus(latestDeployment.status);
  const runFailed =
    latestDeployment != null &&
    (latestDeployment.status === "failed" ||
      latestDeployment.status === "partial_failure");

  const publicUrl = stage.publicUrl;

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="font-medium text-ink">
            {stage.stage}
            {stage.isProduction && (
              <StatusPill tone="warning" dot={false} className="ml-2">
                Production
              </StatusPill>
            )}
            {awaitingClaim && (
              <StatusPill tone="warning" className="ml-2">
                Awaiting claim
              </StatusPill>
            )}
            {stage.isDestroyed && (
              <StatusPill tone="neutral" className="ml-2">
                Destroyed
              </StatusPill>
            )}
            {stage.licenseStatus === "suspended" && (
              <StatusPill tone="danger" className="ml-2">
                Licence suspended
              </StatusPill>
            )}
          </h3>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-sm text-brand-600 hover:underline"
          >
            {publicUrl.replace(/^https:\/\//, "")}
          </a>
          {stage.deployedBuildId && (
            <div className="mt-2 rounded border border-line bg-sunken px-3 py-2 text-xs text-ink-muted">
              <span className="font-medium text-ink">Deployed version: </span>
              <span className="font-mono">{stage.deployedBuildId}</span>
              {stage.deployedAt ? (
                <span className="ml-2 text-ink-muted">
                  ({formatDateTime(stage.deployedAt, { precision: "minute" })})
                </span>
              ) : null}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* A wrapper, not a width class on the select: the select fills its
              container, and this container is as wide as its longest option. */}
          <div>
            <Select
              aria-label="Version to deploy"
              value={selectedBuildId}
              disabled={deployMut.isPending || !!stage.isDestroyed}
              onChange={(e) => setSelectedBuildId(e.target.value)}
              title={
                stage.isProduction
                  ? "A production stage only deploys builds from the production channel"
                  : "Choose a version, or leave \"latest version (production)\""
              }
              options={[
                { value: "", label: "latest version (production)" },
                ...(buildsQuery.data?.items ?? []).map((b) => ({
                  value: b.buildId,
                  label: `${b.buildId} · ${b.channel} · ${b.branch}`,
                })),
              ]}
            />
          </div>
          <Button
            variant="primary"
            loading={deployMut.isPending} disabled={!environmentReady || !connectionReady || !!stage.isDestroyed}
            onClick={() => {
              licenseMut.reset();
              deployMut.mutate();
            }}
            title={
              stage.isDestroyed
                ? "Stage destroyed — delete it, then create a new one to redeploy"
                : !environmentReady
                  ? "The stage must first be claimed via the installation link"
                  : !connectionReady
                    ? "Login not ready yet — re-register the stage"
                    : `Deploy ${stage.stage}`
            }
          >
            {deployMut.isPending ? "Starting…" : "Deploy"}
          </Button>
          <KebabMenu label={`Actions for ${stage.stage}`}>
            <MenuItem
              icon={Pencil}
              onClick={() =>
                navigate(`/tenants/${tenantId}/stages/${stage.stage}/edit`)
              }
            >
              Edit
            </MenuItem>
            <MenuItem
              icon={suspended ? CirclePlay : CirclePause}
              disabled={licenseMut.isPending || !!stage.isDestroyed}
              onClick={() => {
                // Reprendre est le geste qui REND le service : hors production
                // il part sans confirmation. Sur une production, la clé est
                // exigée par le backend dans les deux sens, donc la modale
                // s'ouvre aussi pour la reprise — sans elle, l'action serait
                // simplement impossible depuis la console.
                if (suspended && !stage.isProduction) {
                  deployMut.reset();
                  licenseMut.mutate({ status: "active" });
                  return;
                }
                setLicenseIntent(suspended ? "active" : "suspended");
              }}
            >
              {/* "…" says a confirmation follows. Resuming outside production
                  is the one gesture here that runs at once. */}
              {suspended
                ? stage.isProduction
                  ? "Resume licence…"
                  : "Resume licence"
                : "Suspend licence…"}
            </MenuItem>
            <MenuDivider label="Danger zone" />
            <MenuItem
              danger
              icon={ServerOff}
              disabled={
                anyDestructivePending || !environmentReady || !!stage.isDestroyed
              }
              title={
                stage.isDestroyed
                  ? "Stage already destroyed — only deletion remains possible"
                  : !environmentReady
                    ? "Nothing to destroy (never claimed)"
                    : undefined
              }
              onClick={() => setShowDestroyConfirm(true)}
            >
              Destroy infrastructure…
            </MenuItem>
            <MenuItem
              danger
              icon={Trash2}
              disabled={anyDestructivePending}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete stage…
            </MenuItem>
            <MenuItem
              danger
              icon={TriangleAlert}
              disabled={anyDestructivePending || forceDeleteBlocked}
              title={
                forceDeleteBlocked
                  ? "Unavailable on a production tenant — destroy the infrastructure first"
                  : "Forces deletion without the safety checks (R&D)"
              }
              onClick={() => setShowForceDeleteConfirm(true)}
            >
              Force delete…
            </MenuItem>
          </KebabMenu>
        </div>
      </div>

      {/* The failure of each action of the header, said under the header, beside its own button:
          the Deploy button, and the licence item of the menu (a resume outside production runs at
          once, with no dialog). Each is its own titled box and running one clears the other's, so
          at most one is on screen. Destroy, delete and force delete are absent on purpose: they
          only run from their confirmation, which shows their error (and, for delete, the "Force
          delete" hint) and drops both on Cancel. An error kept here would survive the dialog it
          belongs to and come back stale when it is reopened. The licence's own dialog shows its
          error too, so the box under the header waits for it to close. */}
      <MutationError mutation={deployMut} title="Could not start the deployment" className="mt-4" />
      {licenseIntent === null && (
        <MutationError mutation={licenseMut} title="Could not update the licence" className="mt-4" />
      )}

      {/* Live progress — keeps the operator on the dashboard during a run. */}
      {latestDeployment && (runActive || runFailed) && (
        <div
          className={`mt-4 rounded-md border p-3 ${
            runFailed ? TONE_SURFACE.danger : TONE_SURFACE.info
          }`}
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-ink">
              {latestDeployment.kind === "destroy" ? "Destroy" : "Deployment"}{" "}
              {runActive ? "in progress" : "failed"} ·{" "}
              <span className="text-ink-muted">
                <RelativeTime epochMs={latestDeployment.triggeredAt} />
              </span>
            </p>
            <Link
              to={`/tenants/${tenantId}/deployments`}
              className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
            >
              History
              <Icon icon={ArrowRight} size="sm" />
            </Link>
          </div>
          {latestDeployment.errorMessage && (
            <pre
              className={`mb-2 whitespace-pre-wrap break-words rounded border p-2 font-mono text-xs ${TONE_INSET.danger}`}
            >
              {latestDeployment.errorMessage}
            </pre>
          )}
          <DeploymentTimeline deployment={latestDeployment} />
        </div>
      )}

      {licenseIntent !== null && (
        <ConfirmModal
          title={
            licenseIntent === "suspended"
              ? `Suspend licence for ${stage.stage}`
              : `Resume licence for ${stage.stage}`
          }
          confirmLabel={
            licenseIntent === "suspended" ? "Suspend licence" : "Resume licence"
          }
          pendingLabel={
            licenseIntent === "suspended" ? "Suspending…" : "Resuming…"
          }
          danger={licenseIntent === "suspended"}
          pending={licenseMut.isPending}
          // Taper la phrase ne protège que du geste destructeur. Reprendre rend
          // le service : la clé de production suffit à en faire un acte.
          confirmPhrase={
            licenseIntent === "suspended" ? expected : undefined
          }
          productionKey={stage.isProduction}
          error={licenseMut.error}
          onCancel={() => {
            licenseMut.reset();
            setLicenseIntent(null);
          }}
          onConfirm={(key) =>
            licenseMut.mutate({ status: licenseIntent, productionKey: key })
          }
        >
          {licenseIntent === "suspended" ? (
            <p>
              The control plane stops issuing licences for this stage. Nothing
              goes off air today: the licence already in place runs to its own
              expiry — <strong>up to 30 days</strong>. When it does expire, the
              tenant console stops answering and the audio streams are forced
              down to a bare mono rendition.
            </p>
          ) : (
            <p>
              The control plane starts issuing licences for this stage again,
              within seconds rather than at tomorrow's pass. A stage whose
              licence has already expired recovers on its next cold start.
            </p>
          )}
        </ConfirmModal>
      )}

      {showDestroyConfirm && (
        <ConfirmModal
          title={`Destroy ${stage.stage}`}
          confirmLabel={`Destroy ${stage.stage}`}
          pendingLabel="Destroying…"
          danger
          pending={destroyMut.isPending}
          confirmPhrase={expected}
          acknowledgement={
            stage.isProduction ? (
              <>
                I understand that this stage is in{" "}
                <strong>production</strong> and that destroying it will
                permanently erase all data, streams and configuration for
                the tenant.
              </>
            ) : undefined
          }
          productionKey={stage.isProduction}
          error={destroyMut.error}
          onCancel={() => {
            destroyMut.reset();
            setShowDestroyConfirm(false);
          }}
          onConfirm={(key) =>
            destroyMut.mutate({
              body: {
                confirm: expected,
                ...(stage.isProduction ? { production_acknowledged: true } : {}),
              },
              productionKey: key,
            })
          }
        >
          <p>
            This action tears down all the infrastructure for this stage
            at the tenant: storage, delivery, compute functions, DNS
            entries and logs. This operation <strong>cannot be undone</strong>.
          </p>
        </ConfirmModal>
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title={`Delete ${stage.stage}`}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          danger
          pending={deleteStageMut.isPending}
          confirmPhrase={expected}
          productionKey={stage.isProduction}
          error={deleteStageMut.error}
          errorHint={
            deleteHistoryBlocked && !forceDeleteBlocked ? (
              <p className="text-xs text-ink-muted">
                Tip: Cancel, then choose{" "}
                <span className="font-medium">Force delete</span> in this
                stage's menu to bypass the safety checks (R&D path).
              </p>
            ) : undefined
          }
          onCancel={() => {
            deleteStageMut.reset();
            setShowDeleteConfirm(false);
          }}
          onConfirm={(key) =>
            deleteStageMut.mutate({ body: { confirm: expected }, productionKey: key })
          }
        >
          <p>
            Removes this stage from the tenant's record. Only allowed
            if no deployment is in progress and the infrastructure has
            already been destroyed (or was never deployed). Does not affect
            infrastructure already in place.
          </p>
        </ConfirmModal>
      )}

      {showForceDeleteConfirm && (
        <ConfirmModal
          title={`Force delete ${stage.stage}`}
          confirmLabel={`Delete ${stage.stage}`}
          pendingLabel="Deleting…"
          danger
          pending={forceDeleteStageMut.isPending}
          confirmPhrase={expected}
          acknowledgement={
            stage.isProduction ? (
              <>
                I understand that this stage is in <strong>production</strong>{" "}
                and that force-deleting it bypasses the safety checks.
              </>
            ) : undefined
          }
          productionKey={stage.isProduction}
          error={forceDeleteStageMut.error}
          onCancel={() => {
            forceDeleteStageMut.reset();
            setShowForceDeleteConfirm(false);
          }}
          onConfirm={(key) =>
            forceDeleteStageMut.mutate({
              body: {
                confirm: expected,
                ...(stage.isProduction ? { production_acknowledged: true } : {}),
              },
              productionKey: key,
            })
          }
        >
          <p>
            Permanently deletes the stage and all its access, bypassing
            the safety checks, even if the infrastructure is in an
            inconsistent state. Reserve this for cases where a regular
            destroy cannot recover the stage. This operation{" "}
            <strong>cannot be undone</strong>.
          </p>
        </ConfirmModal>
      )}

      {awaitingClaim && (
        <div className="mt-4 space-y-2">
          {onboardingQuery.isError && <ErrorBox error={onboardingQuery.error} />}
          {onboardingQuery.data && (
            <LaunchStackPanel
              url={onboardingQuery.data.launchStackUrl}
              expiresAt={onboardingQuery.data.expiresAt}
            />
          )}
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-ink-muted">
              Regenerates the link only if it has expired.
            </span>
            <Button
              variant="ghost" size="sm"
              loading={regenerateMut.isPending}
              onClick={() => regenerateMut.mutate()}
            >
              {regenerateMut.isPending ? "Refreshing…" : "Refresh link"}
            </Button>
          </div>
          <MutationError mutation={regenerateMut} title="Could not refresh the installation link" />
        </div>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-ink-muted">
            Status
          </dt>
          <dd className="mt-0.5 flex flex-wrap items-center gap-2">
            <ReadinessBadge
              ready={environmentReady}
              readyLabel="Claimed"
              pendingLabel="Pending"
            />
            <ReadinessBadge
              ready={connectionReady}
              readyLabel="Login ready"
              pendingLabel="Login pending"
            />
            {latestDeployment && (
              <DeploymentStatusBadge status={latestDeployment.status} />
            )}
          </dd>
        </div>
        <DetailRow
          label="Enabled options"
          value={
            stage.enabledOptions.length > 0
              ? stage.enabledOptions.join(", ")
              : "—"
          }
        />
        <BoolPill
          label="Diagnostics (GUI)"
          on={stage.diagnosticsUiEnabled}
        />
        <BoolPill
          label="Companion apps (GUI)"
          on={stage.companionAppsEnabled}
        />
        <BoolPill label="Advertising" on={stage.advertisingEnabled} />
        <AllowlistField
          label="Allowed cloud instances"
          allowlist={stage.allowedGatewayTypes}
          kind="cloud"
          catalogIndex={catalogIndex}
        />
        <AllowlistField
          label="Allowed customer instances"
          allowlist={stage.allowedCustomerInstances}
          kind="customer"
          catalogIndex={catalogIndex}
        />
      </dl>

      {/* A recessed panel, not a second bare <dl> like the block above: the
          licence is the one fact here an operator checks under pressure
          (an expired or unresolved licence takes the environment down), and
          it used to sit in a grid indistinguishable from Diagnostics or
          Companion apps. Still a <dl> — <dt>/<dd> stay valid for a screen
          reader only inside one. */}
      <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 rounded-md border border-line bg-sunken p-4 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-ink-muted">
            Licence issued
          </dt>
          <dd>
            {stage.licenseStatus === "suspended" ? "Suspended — " : ""}
            {stage.issuedExpiresAt
              ? `expires ${formatDate(fromEpochSeconds(stage.issuedExpiresAt))}`
              : "never issued"}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-ink-muted">
            Licence resolved
          </dt>
          <dd>
            {/* "never probed" (no reading at all) and "unreachable" (a
                reading happened and the stage did not answer) are two
                different facts — the raw probeState/probeDetail text keeps
                them apart. valid stays plain; anything else (expired,
                invalid, unreachable) gets the same warning treatment as the
                "Licence suspended" pill above so a mismatch with the
                issued column jumps out. The "resolves" date below is what
                actually confronts "Licence issued": a renewed licence whose
                probe hasn't propagated yet still reads "valid" (old
                probeExpiresAt not yet passed) while the issued date already
                moved — only the two dates side by side expose that drift. */}
            {stage.probeState ? (
              <span
                className={
                  stage.probeState === "valid"
                    ? "text-ink"
                    : `rounded px-1.5 py-0.5 font-medium ${TONE_FILL.danger}`
                }
              >
                {stage.probeState}
                {stage.probeDetail ? ` — ${stage.probeDetail}` : ""}
              </span>
            ) : (
              "never probed"
            )}
            {stage.probeExpiresAt && (
              <span className="ml-2 text-xs text-ink-muted">
                resolves {formatDate(fromEpochSeconds(stage.probeExpiresAt))}
              </span>
            )}
            {stage.probeAt && (
              <span className="ml-2 text-xs text-ink-muted">
                <RelativeTime epochMs={stage.probeAt} />
              </span>
            )}
          </dd>
        </div>
      </dl>

    </div>
  );
}

/** After a directory's name on the sheet: said only when its signing certificate needs someone's attention. */
function certificateSuffix(expiresAt: number | null): string {
  const expiry = signingCertificateExpiry(expiresAt);
  if (expiry.state === "expired") return " — certificate expired";
  if (expiry.state === "soon") return ` — certificate expires ${inDays(expiry.daysLeft)}`;
  return "";
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className={mono ? "font-mono text-ink" : "text-ink"}>
        {value}
      </dd>
    </div>
  );
}

/**
 * A yes/no fact, as a pill rather than a bare word. "enabled"/"disabled" used
 * to sit at the same visual weight as their own all-caps label — the eye had
 * nothing to tell a VALUE from a LABEL. `dot` (default true) is what makes
 * this read as a STATE rather than a tag: see StatusPill's own doc for why
 * `dot={false}` is for a category instead.
 */
function BoolPill({ label, on }: { label: string; on: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-0.5">
        <StatusPill tone={on ? "success" : "neutral"}>
          {on ? "Enabled" : "Disabled"}
        </StatusPill>
      </dd>
    </div>
  );
}

/**
 * Collapsed-by-default disclosure with the non-sensitive technical context.
 * The raw internal identifiers (account id, role ARN, pool/client ids, VPC
 * range) are no longer sent to the GUI at all — they stay server-side — so
 * this only surfaces the few neutral attributes that remain useful.
 */
function TechnicalDetails({
  metadata,
  stages,
}: {
  metadata: TenantMetadata;
  stages: TenantStage[];
}) {
  return (
    <details className="rounded-lg border border-line bg-surface">
      <summary className="cursor-pointer px-6 py-3 text-sm font-medium text-ink-muted hover:text-ink">
        Technical details
      </summary>
      <div className="space-y-4 border-t border-line px-6 py-4 text-xs">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-2">
          <DetailRow label="Identifier" value={metadata.tenantId} mono />
          <DetailRow label="Own partnership" value={metadata.ownPartnershipId} mono />
          <DetailRow label="Debug" value={metadata.debug ? "on" : "off"} />
          <DetailRow
            label="Cost estimation (GUI)"
            value={metadata.costEstimationUiEnabled ? "enabled" : "disabled"}
          />
        </dl>
        {stages.map((s) => (
          <dl
            key={s.stage}
            className="grid grid-cols-2 gap-x-8 gap-y-2 border-t border-line pt-3"
          >
            <DetailRow label={`${s.stage} · region`} value={s.awsRegion} mono />
            <DetailRow
              label={`${s.stage} · URL`}
              value={s.publicUrl.replace(/^https:\/\//, "")}
              mono
            />
          </dl>
        ))}
      </div>
    </details>
  );
}

/**
 * Catalog snapshot grouped by ``kind#model`` → set of published versions.
 * Used to spot orphan references in tenant allowlists.
 */
type CatalogIndex = Map<string, Set<string>>;

function buildCatalogIndex(items: GatewayCatalogItem[]): CatalogIndex {
  const index: CatalogIndex = new Map();
  for (const item of items) {
    if (item.kind === "section" || !item.version) continue;
    const key = `${item.kind}#${item.model}`;
    let versions = index.get(key);
    if (!versions) {
      versions = new Set();
      index.set(key, versions);
    }
    versions.add(item.version);
  }
  return index;
}

type OrphanState =
  | { ok: true }
  | { ok: false; reason: "model-missing" }
  | { ok: false; reason: "versions-missing"; missing: string[] };

function classifyAllowlistEntry(
  kind: GatewayInstanceKind,
  model: string,
  versions: string[],
  catalogIndex: CatalogIndex,
): OrphanState {
  const known = catalogIndex.get(`${kind}#${model}`);
  if (!known || known.size === 0) {
    return { ok: false, reason: "model-missing" };
  }
  if (versions.includes("*")) {
    return { ok: true };
  }
  const missing = versions.filter((v) => !known.has(v));
  if (missing.length === 0) {
    return { ok: true };
  }
  return { ok: false, reason: "versions-missing", missing };
}

function AllowlistField({
  label,
  allowlist,
  kind,
  catalogIndex,
}: {
  label: string;
  allowlist: Record<string, string[]> | null;
  kind: GatewayInstanceKind;
  catalogIndex: CatalogIndex;
}) {
  return (
    <div className="col-span-2">
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-1 text-ink">
        {!allowlist || Object.keys(allowlist).length === 0 ? (
          <span className="text-ink-muted">none</span>
        ) : (
          // A bordered ROW per entry, not a pill: a pill is what a STATE wears
          // (BoolPill above), and this is a set membership list — the two must
          // not share a shape, or "Allowed customer instances" and "Companion
          // apps off" read as the same kind of fact at a glance.
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(allowlist).map(([model, versions]) => {
              const state = classifyAllowlistEntry(
                kind,
                model,
                versions,
                catalogIndex,
              );
              const isOrphan = !state.ok;
              const title = !state.ok
                ? state.reason === "model-missing"
                  ? `No ${kind}/${model} entry in the catalog — orphan reference, should be removed.`
                  : `Versions missing from the catalog: ${state.missing.join(", ")} — remove or update.`
                : undefined;
              return (
                <span
                  key={model}
                  title={title}
                  className={`flex items-center gap-1.5 rounded border px-2 py-1 font-mono text-xs ${
                    isOrphan
                      ? `text-warning-fg ${TONE_SURFACE.warning}`
                      : "border-line text-ink"
                  }`}
                >
                  <Icon
                    icon={isOrphan ? TriangleAlert : CircleCheck}
                    size="sm"
                    label={isOrphan ? "orphan reference" : undefined}
                    className={isOrphan ? "" : "text-success-dot"}
                  />
                  {model}
                </span>
              );
            })}
          </div>
        )}
      </dd>
    </div>
  );
}
